'use client';

/**
 * Profile photo — `POST` / `DELETE /api/v1/users/me/avatar`.
 *
 * The server is the authority (magic bytes, 5 MB, ClamAV, squared to 512 px
 * with EXIF stripped, a private key served back through
 * `/api/v1/user-media/avatars/…`); `checkAvatarFile` only turns a wrong file
 * away before the upload. Both hooks invalidate the users/me query and
 * *return* that promise, so a caller's own `onSuccess` sees the new
 * `avatar_url` — the same rule as `usePatchProfile`.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useApi } from '@/lib/api';
import { validateImageFile } from '@/lib/blog/images';

import { USERS_ME_QUERY_KEY } from './useUsersMe';

export interface AvatarResult {
  avatar_url: string | null;
}

/** File-picker `accept` — the server's allow-list. */
export const AVATAR_ACCEPT = 'image/jpeg,image/png,image/webp';

/**
 * Pre-flight for a picked photo: the blog image rules (same three types,
 * same 5 MB), with sentences about a profile photo rather than the public
 * blog. `null` when the file may go up.
 */
export async function checkAvatarFile(file: File): Promise<string | null> {
  const check = await validateImageFile(file);
  if (check.ok) return null;
  switch (check.code) {
    case 'svg':
      return 'SVG files are not accepted. Choose a JPEG, PNG or WebP photo.';
    case 'empty':
      return 'That file is empty.';
    case 'too_large':
      return `That photo is ${(file.size / (1024 * 1024)).toFixed(1)} MB. The maximum is 5 MB.`;
    default:
      return 'Only JPEG, PNG and WebP photos are accepted.';
  }
}

/** Initials for the circle shown when there is no photo. */
export function avatarInitials(source: string | null | undefined): string {
  const parts = (source ?? '')
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2);
  if (parts.length === 0) return '··';
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('');
}

export function useUploadAvatar() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<AvatarResult, Error, File>({
    mutationFn: (file) => {
      const form = new FormData();
      form.append('file', file);
      return api<AvatarResult>('/users/me/avatar', { method: 'POST', body: form });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_ME_QUERY_KEY }),
  });
}

export function useRemoveAvatar() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<AvatarResult, Error, void>({
    mutationFn: () => api<AvatarResult>('/users/me/avatar', { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_ME_QUERY_KEY }),
  });
}
